import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ArrowLeft, Save, User } from 'lucide-react';
import { toast } from 'sonner';

const ISPAddCustomer = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [customerData, setCustomerData] = useState({
    company_name: '',
    contact_person: '',
    mobile: '',
    email: '',
    password: '',
    gst_number: '',
    installation_address: '',
    connection_type: 'FTTH',
    static_ip: '',
    account_manager: ''
  });

  const [planData, setPlanData] = useState({
    plan_name: '',
    download_speed: '',
    upload_speed: '',
    plan_cost: '',
    activation_date: '',
    expiry_date: ''
  });

  const handleCustomerChange = (e) => {
    setCustomerData({ ...customerData, [e.target.name]: e.target.value });
  };

  const handlePlanChange = (e) => {
    setPlanData({ ...planData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      
      // Convert dates to ISO format
      const payload = {
        ...customerData,
        plan_name: planData.plan_name,
        download_speed: planData.download_speed,
        upload_speed: planData.upload_speed,
        plan_cost: parseFloat(planData.plan_cost),
        activation_date: new Date(planData.activation_date).toISOString(),
        expiry_date: new Date(planData.expiry_date).toISOString()
      };

      const response = await fetch('/api/admin/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Customer created successfully!');
        navigate('/isp/users');
      } else {
        toast.error(data.detail || 'Error creating customer');
      }
    } catch (error) {
      toast.error('Error creating customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-6 px-4 shadow-lg">
        <div className="container mx-auto">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/isp/users')}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <User className="w-8 h-8" />
                Add New Customer
              </h1>
              <p className="text-blue-200 mt-1">Create a new ISP customer account</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Customer Information */}
            <Card className="shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <CardTitle>Customer Information</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label>Company Name *</Label>
                  <Input
                    name="company_name"
                    value={customerData.company_name}
                    onChange={handleCustomerChange}
                    required
                  />
                </div>

                <div>
                  <Label>Contact Person *</Label>
                  <Input
                    name="contact_person"
                    value={customerData.contact_person}
                    onChange={handleCustomerChange}
                    required
                  />
                </div>

                <div>
                  <Label>Mobile Number *</Label>
                  <Input
                    name="mobile"
                    value={customerData.mobile}
                    onChange={handleCustomerChange}
                    required
                  />
                </div>

                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={customerData.email}
                    onChange={handleCustomerChange}
                    required
                  />
                </div>

                <div>
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    name="password"
                    value={customerData.password}
                    onChange={handleCustomerChange}
                    required
                  />
                </div>

                <div>
                  <Label>GST Number</Label>
                  <Input
                    name="gst_number"
                    value={customerData.gst_number}
                    onChange={handleCustomerChange}
                  />
                </div>

                <div>
                  <Label>Installation Address *</Label>
                  <Input
                    name="installation_address"
                    value={customerData.installation_address}
                    onChange={handleCustomerChange}
                    required
                  />
                </div>

                <div>
                  <Label>Connection Type *</Label>
                  <select
                    name="connection_type"
                    value={customerData.connection_type}
                    onChange={handleCustomerChange}
                    className="w-full border rounded-md px-3 py-2"
                    required
                  >
                    <option value="FTTH">FTTH</option>
                    <option value="Leased Line">Leased Line</option>
                    <option value="Wireless">Wireless</option>
                  </select>
                </div>

                <div>
                  <Label>Static IP</Label>
                  <Input
                    name="static_ip"
                    value={customerData.static_ip}
                    onChange={handleCustomerChange}
                    placeholder="e.g., 103.x.x.x"
                  />
                </div>

                <div>
                  <Label>Account Manager</Label>
                  <Input
                    name="account_manager"
                    value={customerData.account_manager}
                    onChange={handleCustomerChange}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Plan Information */}
            <Card className="shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <CardTitle>Plan Information</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label>Plan Name *</Label>
                  <Input
                    name="plan_name"
                    value={planData.plan_name}
                    onChange={handlePlanChange}
                    placeholder="e.g., Enterprise 100"
                    required
                  />
                </div>

                <div>
                  <Label>Download Speed *</Label>
                  <Input
                    name="download_speed"
                    value={planData.download_speed}
                    onChange={handlePlanChange}
                    placeholder="e.g., 100 Mbps"
                    required
                  />
                </div>

                <div>
                  <Label>Upload Speed *</Label>
                  <Input
                    name="upload_speed"
                    value={planData.upload_speed}
                    onChange={handlePlanChange}
                    placeholder="e.g., 100 Mbps"
                    required
                  />
                </div>

                <div>
                  <Label>Plan Cost (₹) *</Label>
                  <Input
                    type="number"
                    name="plan_cost"
                    value={planData.plan_cost}
                    onChange={handlePlanChange}
                    placeholder="e.g., 5000"
                    required
                  />
                </div>

                <div>
                  <Label>Activation Date *</Label>
                  <Input
                    type="date"
                    name="activation_date"
                    value={planData.activation_date}
                    onChange={handlePlanChange}
                    required
                  />
                </div>

                <div>
                  <Label>Expiry Date *</Label>
                  <Input
                    type="date"
                    name="expiry_date"
                    value={planData.expiry_date}
                    onChange={handlePlanChange}
                    required
                  />
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-6"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {loading ? 'Creating Customer...' : 'Create Customer'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ISPAddCustomer;
